import { motion } from 'framer-motion';
import { useState } from 'react';
import { Mail, User, Calendar, MapPin, Loader2, CheckCircle } from 'lucide-react';
import { submitWaitlist } from '../api/waitlist';
import type { WaitlistSubmission } from '../types';

interface WaitlistProps {
    onSignupSuccess: () => void;
}

export default function Waitlist({ onSignupSuccess }: WaitlistProps) {
    const [formData, setFormData] = useState<WaitlistSubmission>({
        email: '',
        name: '',
        ageRange: '',
        referralSource: '',
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false);
    const [error, setError] = useState('');
    const [position, setPosition] = useState<number | null>(null);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setIsSubmitting(true);

        try {
            const response = await submitWaitlist(formData);
            setIsSuccess(true);
            setPosition(response.position || null);
            onSignupSuccess();

            // Reset form
            setFormData({
                email: '',
                name: '',
                ageRange: '',
                referralSource: '',
            });
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to join waitlist. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    if (isSuccess) {
        return (
            <section id="waitlist" className="py-20 gradient-bg relative overflow-hidden">
                {/* Accent decoration */}
                <div className="absolute inset-0 overflow-hidden pointer-events-none">
                    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-primary-500 rounded-full opacity-10 blur-3xl" />
                </div>

                <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                    <motion.div
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.6 }}
                        className="max-w-2xl mx-auto text-center"
                    >
                        <div className="bg-dark-800 border-2 border-primary-500/50 rounded-3xl p-12 shadow-2xl shadow-primary-500/20">
                            <div className="inline-flex items-center justify-center w-20 h-20 bg-green-500/20 border border-green-500/50 rounded-full mb-6">
                                <CheckCircle className="w-10 h-10 text-green-400" />
                            </div>

                            <h2 className="font-display text-3xl sm:text-4xl font-bold text-white mb-4">You're In!</h2>

                            {position && (
                                <p className="text-xl text-gray-300 mb-6">
                                    You're <strong className="text-gradient">#{position}</strong> on the waitlist
                                </p>
                            )}

                            <p className="text-gray-400 mb-8">
                                We'll email you as soon as DareUp launches. Get ready to start creating!
                            </p>

                            <div className="bg-primary-500/10 border border-primary-500/30 rounded-xl p-6">
                                <p className="text-sm text-gray-300">
                                    <strong className="text-white">Pro Tip:</strong> Share DareUp with your friends to
                                    move up in line and get exclusive beta access!
                                </p>
                            </div>
                        </div>
                    </motion.div>
                </div>
            </section>
        );
    }

    return (
        <section id="waitlist" className="py-20 gradient-bg relative overflow-hidden">
            {/* Accent decoration */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-gradient-to-r from-primary-500 to-secondary-500 rounded-full opacity-10 blur-3xl" />
            </div>

            <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6 }}
                    className="max-w-2xl mx-auto"
                >
                    <div className="text-center mb-12">
                        <h2 className="font-display text-4xl sm:text-5xl font-bold text-white mb-4">
                            Be First to Dare
                        </h2>
                        <p className="text-xl text-gray-400">Join the waitlist for exclusive early access</p>
                    </div>

                    <div className="bg-dark-800 border-2 border-primary-500/50 rounded-3xl p-8 sm:p-12 shadow-2xl shadow-primary-500/20">
                        <form onSubmit={handleSubmit} className="space-y-6">
                            {/* Email */}
                            <div>
                                <label htmlFor="email" className="block text-sm font-semibold text-gray-300 mb-2">
                                    Email Address <span className="text-primary-400">*</span>
                                </label>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                                        <Mail className="w-5 h-5 text-gray-500" />
                                    </div>
                                    <input
                                        type="email"
                                        id="email"
                                        required
                                        value={formData.email}
                                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                        className="w-full pl-12 pr-4 py-3 bg-dark-900 border-2 border-dark-700 text-white rounded-xl focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 outline-none transition-all placeholder-gray-500"
                                        placeholder="you@example.com"
                                    />
                                </div>
                            </div>

                            {/* Name */}
                            <div>
                                <label htmlFor="name" className="block text-sm font-semibold text-gray-300 mb-2">
                                    Name <span className="text-gray-500 text-xs">(optional)</span>
                                </label>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                                        <User className="w-5 h-5 text-gray-500" />
                                    </div>
                                    <input
                                        type="text"
                                        id="name"
                                        value={formData.name}
                                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                        className="w-full pl-12 pr-4 py-3 bg-dark-900 border-2 border-dark-700 text-white rounded-xl focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 outline-none transition-all placeholder-gray-500"
                                        placeholder="Your name"
                                    />
                                </div>
                            </div>

                            {/* Age Range */}
                            <div>
                                <label htmlFor="ageRange" className="block text-sm font-semibold text-gray-300 mb-2">
                                    Age Range <span className="text-gray-500 text-xs">(optional)</span>
                                </label>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                                        <Calendar className="w-5 h-5 text-gray-500" />
                                    </div>
                                    <select
                                        id="ageRange"
                                        value={formData.ageRange}
                                        onChange={(e) => setFormData({ ...formData, ageRange: e.target.value })}
                                        className="w-full pl-12 pr-4 py-3 bg-dark-900 border-2 border-dark-700 text-white rounded-xl focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 outline-none transition-all appearance-none"
                                    >
                                        <option value="">Select your age range</option>
                                        <option value="13-15">13-15</option>
                                        <option value="16-20">16-20</option>
                                        <option value="21-25">21-25</option>
                                        <option value="26-30">26-30</option>
                                        <option value="31+">31+</option>
                                    </select>
                                </div>
                            </div>

                            {/* Referral Source */}
                            <div>
                                <label
                                    htmlFor="referralSource"
                                    className="block text-sm font-semibold text-gray-300 mb-2"
                                >
                                    How did you hear about us? <span className="text-gray-500 text-xs">(optional)</span>
                                </label>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                                        <MapPin className="w-5 h-5 text-gray-500" />
                                    </div>
                                    <select
                                        id="referralSource"
                                        value={formData.referralSource}
                                        onChange={(e) => setFormData({ ...formData, referralSource: e.target.value })}
                                        className="w-full pl-12 pr-4 py-3 bg-dark-900 border-2 border-dark-700 text-white rounded-xl focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 outline-none transition-all appearance-none"
                                    >
                                        <option value="">Select a source</option>
                                        <option value="social-media">Social Media</option>
                                        <option value="friend">Friend/Family</option>
                                        <option value="search">Search Engine</option>
                                        <option value="news">News/Blog</option>
                                        <option value="other">Other</option>
                                    </select>
                                </div>
                            </div>

                            {/* Error message */}
                            {error && (
                                <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4">
                                    <p className="text-sm text-red-400">{error}</p>
                                </div>
                            )}

                            {/* Submit button */}
                            <button
                                type="submit"
                                disabled={isSubmitting}
                                className="w-full py-4 bg-gradient-to-r from-primary-500 to-secondary-500 text-white rounded-xl font-bold text-lg shadow-lg shadow-primary-500/30 hover:shadow-xl hover:shadow-primary-500/50 transition-all duration-300 hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 flex items-center justify-center gap-2"
                            >
                                {isSubmitting ? (
                                    <>
                                        <Loader2 className="w-5 h-5 animate-spin" />
                                        Joining...
                                    </>
                                ) : (
                                    'Reserve My Spot'
                                )}
                            </button>

                            {/* Privacy note */}
                            <p className="text-xs text-gray-500 text-center">
                                We'll never spam you. One email when we launch.
                                <br />
                                <strong className="text-gray-300">
                                    First 1,000 signups get exclusive beta access + founder badge!
                                </strong>
                            </p>
                        </form>
                    </div>
                </motion.div>
            </div>
        </section>
    );
}
